// Service worker registration with smart update handling for PWA
// Implements graceful updates that don't interrupt user activity
// Learn more: https://cra.link/PWA

const isLocalhost = Boolean(
	window.location.hostname === 'localhost' ||
	// [::1] is the IPv6 localhost address.
	window.location.hostname === '[::1]' ||
	// 127.0.0.0/8 are considered localhost for IPv4.
	window.location.hostname.match(/^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/)
)

type Config = {
	onSuccess?: (registration: ServiceWorkerRegistration) => void
	onUpdate?: (registration: ServiceWorkerRegistration) => void
}

// Smart reload configuration - waits for user idle before updating
const IDLE_THRESHOLD_MS = 5000      // 5 seconds of inactivity = idle
const EXTRA_IDLE_DELAY_MS = 10000   // Additional 10 seconds grace period
const MAX_WAIT_MS = 5 * 60 * 1000   // Maximum 5 minutes wait before forcing update
const UPDATE_CHECK_INTERVAL = 60 * 60 * 1000 // Check for updates every hour

// Track user activity for smart reload
let last_activity = Date.now()
let update_detected_at = 0
let pending_worker: ServiceWorker | null = null
let smart_reload_interval: ReturnType<typeof setInterval> | null = null

// BroadcastChannel for multi-tab coordination
let update_channel: BroadcastChannel | null = null

const track_activity = () => {
	last_activity = Date.now()
}

const init_activity_tracking = () => {
	const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click']
	events.forEach(evt => {
		document.addEventListener(evt, track_activity, { passive: true })
	})
}

const cleanup_activity_tracking = () => {
	const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click']
	events.forEach(evt => {
		document.removeEventListener(evt, track_activity)
	})
}

const is_user_idle = () => {
	return Date.now() - last_activity > IDLE_THRESHOLD_MS
}

const should_reload_now = () => {
	if (!update_detected_at) return false

	const waited = Date.now() - update_detected_at
	const idle_long = is_user_idle() && waited > IDLE_THRESHOLD_MS + EXTRA_IDLE_DELAY_MS

	// Reload if: tab is hidden, user has been idle long enough, or max wait exceeded
	return document.hidden || idle_long || waited > MAX_WAIT_MS
}

const trigger_skip_waiting_and_reload = () => {
	if (pending_worker) {
		console.log('Triggering service worker update...')
		pending_worker.postMessage({ type: 'SKIP_WAITING' })
		pending_worker = null
		update_detected_at = 0
	}
}

const check_and_reload = () => {
	if (pending_worker && should_reload_now()) {
		trigger_skip_waiting_and_reload()
	}
}

const start_smart_reload_check = () => {
	if (smart_reload_interval) return

	smart_reload_interval = setInterval(check_and_reload, 1000)
}

const stop_smart_reload_check = () => {
	if (smart_reload_interval) {
		clearInterval(smart_reload_interval)
		smart_reload_interval = null
	}
}

const broadcast_update_ready = () => {
	try {
		if (!update_channel) {
			update_channel = new BroadcastChannel('sw-update')
		}
		update_channel.postMessage('UPDATE_READY')
	} catch (e) {
		// BroadcastChannel not supported in some browsers
		console.log('BroadcastChannel not available')
	}
}

const setup_broadcast_listener = () => {
	try {
		if (!update_channel) {
			update_channel = new BroadcastChannel('sw-update')
		}
		update_channel.addEventListener('message', (ev) => {
			if (ev.data === 'UPDATE_READY' && pending_worker) {
				start_smart_reload_check()
			}
		})
	} catch (e) {
		// BroadcastChannel not supported
	}
}

export function register(config?: Config) {
	if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
		// The URL constructor is available in all browsers that support SW.
		const publicUrl = new URL(process.env.PUBLIC_URL, window.location.href)
		if (publicUrl.origin !== window.location.origin) {
			// Our service worker won't work if PUBLIC_URL is on a different origin
			// from what our page is served on. This might happen if a CDN is used to
			// serve assets; see https://github.com/facebook/create-react-app/issues/2374
			return
		}

		// Initialize activity tracking for smart reload
		init_activity_tracking()
		setup_broadcast_listener()

		window.addEventListener('load', () => {
			const swUrl = `${process.env.PUBLIC_URL}/service-worker.js`

			if (isLocalhost) {
				// This is running on localhost. Let's check if a service worker still exists or not.
				checkValidServiceWorker(swUrl, config)

				// Add some additional logging to localhost, pointing developers to the
				// service worker/PWA documentation.
				navigator.serviceWorker.ready.then(() => {
					console.log(
						'This web app is being served cache-first by a service ' +
						'worker. To learn more, visit https://cra.link/PWA'
					)
				})
			} else {
				// Is not localhost. Just register service worker
				registerValidSW(swUrl, config)
			}
		})

		// Listen for controller change and reload when new SW takes over
		navigator.serviceWorker.addEventListener('controllerchange', () => {
			console.log('New service worker activated, reloading...')
			window.location.reload()
		})
	}
}

function registerValidSW(swUrl: string, config?: Config) {
	navigator.serviceWorker
		.register(swUrl)
		.then((registration) => {
			// Check if there's already a waiting worker from a previous visit
			if (registration.waiting) {
				pending_worker = registration.waiting
				update_detected_at = Date.now()
				broadcast_update_ready()
				start_smart_reload_check()

				if (config && config.onUpdate) {
					config.onUpdate(registration)
				}
			}

			registration.onupdatefound = () => {
				const installingWorker = registration.installing
				if (installingWorker == null) {
					return
				}
				installingWorker.onstatechange = () => {
					if (installingWorker.state === 'installed') {
						if (navigator.serviceWorker.controller) {
							// New content is available
							console.log('New content is available and will update when safe')

							pending_worker = installingWorker
							update_detected_at = Date.now()

							// Broadcast to other tabs
							broadcast_update_ready()

							// Start smart reload checking
							start_smart_reload_check()

							// Execute callback
							if (config && config.onUpdate) {
								config.onUpdate(registration)
							}
						} else {
							// Content is cached for offline use
							console.log('Content is cached for offline use.')

							// Execute callback
							if (config && config.onSuccess) {
								config.onSuccess(registration)
							}
						}
					}
				}
			}

			// Periodic update checks (every hour)
			setInterval(() => {
				console.log('Checking for service worker updates...')
				registration.update().catch((error) => {
					console.log('Service worker update check failed:', error)
				})
			}, UPDATE_CHECK_INTERVAL)

			// Also check for updates when page becomes visible (for iOS)
			document.addEventListener('visibilitychange', () => {
				if (!document.hidden) {
					registration.update().catch((error) => {
						console.log('Service worker update check failed:', error)
					})
				}
			})
		})
		.catch((error) => {
			console.error('Error during service worker registration:', error)
		})
}

function checkValidServiceWorker(swUrl: string, config?: Config) {
	// Check if the service worker can be found. If it can't reload the page.
	fetch(swUrl, {
		headers: { 'Service-Worker': 'script' },
	})
		.then((response) => {
			// Ensure service worker exists, and that we really are getting a JS file.
			const contentType = response.headers.get('content-type')
			if (
				response.status === 404 ||
				(contentType != null && contentType.indexOf('javascript') === -1)
			) {
				// No service worker found. Probably a different app. Reload the page.
				navigator.serviceWorker.ready.then((registration) => {
					registration.unregister().then(() => {
						window.location.reload()
					})
				})
			} else {
				// Service worker found. Proceed as normal.
				registerValidSW(swUrl, config)
			}
		})
		.catch(() => {
			console.log('No internet connection found. App is running in offline mode.')
		})
}

export function unregister() {
	if ('serviceWorker' in navigator) {
		// Clean up
		cleanup_activity_tracking()
		stop_smart_reload_check()
		if (update_channel) {
			update_channel.close()
			update_channel = null
		}

		navigator.serviceWorker.ready
			.then((registration) => {
				registration.unregister()
			})
			.catch((error) => {
				console.error(error.message)
			})
	}
}

// Force immediate update - can be called from console or UI for testing
export function forceUpdate() {
	if (pending_worker) {
		console.log('Forcing immediate update...')
		trigger_skip_waiting_and_reload()
	} else {
		console.log('No pending update available')
	}
}
